import type { Rect, UiRects } from "../../game/input";
import { CANVAS_WIDTH } from "../../game/constants";
import type { RenderModel } from "../renderModel";
import type { PhoneMetrics } from "../renderConstants";
import {
  GAME_FONT,
  mFont,
  resolvePhoneMetrics,
  roundedRectPath,
} from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface PauseMenuDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  pauseMenuClosing: boolean;
  pauseMenuCloseMs: number;
  pauseMenuOpenMs: number;
  nowMs: () => number;
  drawPhoneSettings: (
    ctx: CanvasRenderingContext2D,
    model: RenderModel,
    uiRects: UiRects,
    scrX: number,
    scrY: number,
    scrW: number,
    scrH: number,
    contentX: number,
    contentW: number,
    contentRight: number,
    metrics: PhoneMetrics,
  ) => void;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawPauseMenuScreen(deps: PauseMenuDeps, model: RenderModel): UiRects {
  const { ctx, canvasHeight: canvasH, nowMs } = deps;
  const uiRects: UiRects = {};
  const px = 2;
  const phoneMetrics = resolvePhoneMetrics();

  // Phone positioned from bottom — top portion visible, bottom off-screen
  const phoneW = Math.min(CANVAS_WIDTH - 24, 340);
  const phoneH = canvasH + 40; // extends below canvas
  const phoneX = CANVAS_WIDTH / 2 - phoneW / 2;
  const phoneY = 80; // top of phone visible area
  const cornerR = 12;
  const bezelW = 6;
  const scrX = phoneX + bezelW;
  const scrY = phoneY + bezelW + 20;
  const scrW = phoneW - bezelW * 2;
  const scrH = canvasH - scrY - 10; // screen extends to near bottom of canvas
  const contentX = scrX + 8;
  const contentW = scrW - 16;
  const contentRight = contentX + contentW;

  // Slide-up from bottom
  let slideOffset: number;
  if (deps.pauseMenuClosing) {
    const closeMs = nowMs() - deps.pauseMenuCloseMs;
    const closeProg = Math.min(1, closeMs / 250);
    const closeEase = closeProg * closeProg;
    slideOffset = closeEase * (canvasH - phoneY + 20);
  } else {
    const slideMs = nowMs() - deps.pauseMenuOpenMs;
    const slideProg = Math.min(1, slideMs / 300);
    const slideEase = 1 - (1 - slideProg) * (1 - slideProg);
    slideOffset = (1 - slideEase) * (canvasH - phoneY + 20);
  }
  ctx.save();
  ctx.translate(0, slideOffset);

  uiRects.pauseMenuBounds = { x: phoneX, y: phoneY, w: phoneW, h: phoneH };

  // Outer phone body (top corners rounded, bottom extends off-screen)
  ctx.beginPath();
  ctx.moveTo(phoneX + cornerR, phoneY);
  ctx.arcTo(phoneX + phoneW, phoneY, phoneX + phoneW, phoneY + cornerR, cornerR);
  ctx.lineTo(phoneX + phoneW, phoneY + phoneH);
  ctx.lineTo(phoneX, phoneY + phoneH);
  ctx.lineTo(phoneX, phoneY + cornerR);
  ctx.arcTo(phoneX, phoneY, phoneX + cornerR, phoneY, cornerR);
  ctx.closePath();
  ctx.fillStyle = "#1a2030";
  ctx.fill();
  ctx.strokeStyle = "#3a4a6a";
  ctx.lineWidth = px;
  ctx.stroke();

  // Inner bezel stroke
  ctx.beginPath();
  ctx.moveTo(phoneX + px + cornerR - 1, phoneY + px);
  ctx.arcTo(phoneX + phoneW - px, phoneY + px, phoneX + phoneW - px, phoneY + px + cornerR - 1, cornerR - 1);
  ctx.lineTo(phoneX + phoneW - px, phoneY + phoneH - px);
  ctx.lineTo(phoneX + px, phoneY + phoneH - px);
  ctx.lineTo(phoneX + px, phoneY + px + cornerR - 1);
  ctx.arcTo(phoneX + px, phoneY + px, phoneX + px + cornerR - 1, phoneY + px, cornerR - 1);
  ctx.closePath();
  ctx.strokeStyle = "#2a3a55";
  ctx.lineWidth = 1;
  ctx.stroke();

  // Screen area
  ctx.fillStyle = "#0c1624";
  ctx.fillRect(scrX, scrY, scrW, scrH);
  // Scanlines
  ctx.save();
  ctx.globalAlpha = 0.03;
  for (let sy = scrY; sy < scrY + scrH; sy += 3) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(scrX, sy, scrW, 1);
  }
  ctx.restore();

  // Status bar
  const statusY = phoneY + bezelW + 2;
  const statusH = 16;
  const d = new Date();
  const timeStr = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  ctx.fillStyle = "#5a7a9a";
  ctx.font = `bold 7px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(timeStr, phoneX + bezelW + 6, statusY + statusH / 2);
  const sigBarX = phoneX + phoneW - bezelW - 30;
  const sigBarY = statusY + 4;
  for (let b = 0; b < 4; b++) {
    const bh = 3 + b * 2;
    ctx.fillStyle = "#4dff88";
    ctx.fillRect(sigBarX + b * 5, sigBarY + (10 - bh), 3, bh);
  }
  const batX = phoneX + phoneW - bezelW - 8;
  ctx.fillStyle = "#4dff88";
  ctx.fillRect(batX, sigBarY + 2, 5, 7);
  ctx.fillRect(batX + 1, sigBarY, 3, 2);

  // ── Screen Content (clipped) ──
  ctx.save();
  ctx.beginPath();
  ctx.rect(scrX, scrY, scrW, scrH);
  ctx.clip();

  if (model.pauseShowSettings) {
    // Reuse phone settings renderer
    deps.drawPhoneSettings(ctx, model, uiRects, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, phoneMetrics);
    // Clear PREV/NEXT rects so they don't steal clicks from the back button
    uiRects.settingsBgPrev = undefined;
    uiRects.settingsBgNext = undefined;
    // Back button at bottom
    const backBtnW = 80;
    const backBtnH = 24;
    const backBtnX = scrX + (scrW - backBtnW) / 2;
    const backBtnY = scrY + scrH - backBtnH - 8;
    const backRect: Rect = { x: backBtnX, y: backBtnY, w: backBtnW, h: backBtnH };
    roundedRectPath(ctx, backRect.x, backRect.y, backRect.w, backRect.h, 4);
    ctx.fillStyle = "rgba(20,36,54,0.95)";
    ctx.fill();
    ctx.strokeStyle = "#64d8ff";
    ctx.lineWidth = 1.5;
    roundedRectPath(ctx, backRect.x, backRect.y, backRect.w, backRect.h, 4);
    ctx.stroke();
    ctx.fillStyle = "#64d8ff";
    ctx.font = mFont("bold", 7);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("\u25C4 BACK", backRect.x + backRect.w / 2, backRect.y + backRect.h / 2);
    uiRects.pauseSettingsBack = backRect;
  } else {
    // Main pause menu
    const centerX = scrX + scrW / 2;

    // "PAUSED" title
    const titleY = scrY + 36;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#64d8ff";
    ctx.font = mFont("bold", 14);
    ctx.fillText("PAUSED", centerX, titleY);

    // Pause icon (two bars)
    const iconY = titleY + 22;
    const iconBarW = 6;
    const iconBarH = 20;
    const iconGap = 8;
    ctx.fillStyle = "rgba(100,216,255,0.4)";
    ctx.fillRect(centerX - iconGap - iconBarW, iconY, iconBarW, iconBarH);
    ctx.fillRect(centerX + iconGap, iconY, iconBarW, iconBarH);

    // 4 buttons
    const btnW = contentW - 20;
    const btnH = 32;
    const btnGap = 10;
    const btnsStartY = iconY + iconBarH + 28;
    const btnX = contentX + (contentW - btnW) / 2;

    const drawPauseBtn = (label: string, color: string, by: number): Rect => {
      const rect: Rect = { x: btnX, y: by, w: btnW, h: btnH };
      roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 6);
      ctx.fillStyle = "rgba(20,36,54,0.85)";
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 6);
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.font = mFont("bold", 8);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2);
      return rect;
    };

    uiRects.pauseResume   = drawPauseBtn("RESUME",   "#4dff88", btnsStartY);
    uiRects.pauseSettings = drawPauseBtn("SETTINGS", "#64d8ff", btnsStartY + (btnH + btnGap));
    uiRects.pauseRestart  = drawPauseBtn("RESTART",  "#ffb347", btnsStartY + 2 * (btnH + btnGap));
    uiRects.pauseQuit     = drawPauseBtn("QUIT",     "#ff6b6b", btnsStartY + 3 * (btnH + btnGap));
  }

  ctx.restore(); // end screen clip

  ctx.restore(); // end slide-up transform

  return uiRects;
}
